import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, FormControlLabel, Checkbox,
  CircularProgress, Typography,
} from '@mui/material';
import { screenService, WmsScreenType } from '../services/screenService';
import { useTranslation } from 'react-i18next';

interface Screen {
  id: string;
  name: string;
  isActive: boolean;
  settings?: string;
}

interface WmsScreenModalProps {
  open: boolean;
  screenType: WmsScreenType;
  warehouseId: string;
  editScreen?: Screen | null;
  onClose: () => void;
  onSaved: () => void;
}

const WmsScreenModal: React.FC<WmsScreenModalProps> = ({
  open, screenType, warehouseId, editScreen, onClose, onSaved,
}) => {
  const { t } = useTranslation('warehouse');
  const [name, setName] = useState('');
  const TYPE_LABELS: Record<string, string> = {
    picking: t('components.wmsScreenModal.typePicking'),
    packing: t('components.wmsScreenModal.typePacking'),
    receiving: t('components.wmsScreenModal.typeReceiving'),
    cyclecount: t('components.wmsScreenModal.typeCycleCount'),
  };

  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editScreen) {
      setName(editScreen.name);
      setIsActive(editScreen.isActive);
    } else {
      setName('');
      setIsActive(true);
    }
    setError('');
  }, [editScreen, open]);

  const handleSave = async () => {
    if (!name.trim()) { setError(t('components.wmsScreenModal.errorNameRequired')); return; }
    setError('');
    setSaving(true);
    try {
      if (editScreen) {
        await screenService.updateScreen(screenType, {
          Id: editScreen.id, Name: name.trim(), IsActive: isActive,
        });
      } else {
        await screenService.createScreen(screenType, {
          WarehouseId: warehouseId, Name: name.trim(), IsActive: isActive,
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || t('components.wmsScreenModal.errorSaveFailed'));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{editScreen ? t('components.wmsScreenModal.editTitle') : t('components.wmsScreenModal.addTitle', { type: TYPE_LABELS[screenType] })}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField size="small" label={t('components.wmsScreenModal.screenNameLabel')} value={name}
            onChange={e => setName(e.target.value)} autoFocus />
          <FormControlLabel control={<Checkbox checked={isActive} onChange={e => setIsActive(e.target.checked)} />}
            label={t('common.active')} />
          {error && <Typography color="error" variant="body2">{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WmsScreenModal;
